<template>
	<div class="pdf">
		<div id="inch1"></div>
    	<div class="pdf-container">
			<div class="pdf-viewer">
				<div class="vertical-ruler" v-show="showRulers">
					<ruler
							:style="[offset.base, offset.v]"
							:unit="unit"
							:length="doc.height.toPixel()"
							:scale="ratio"
							:zoom="zoom"
							orientation="vertical"></ruler>
				</div>
				<div class="horizontal-ruler" v-show="showRulers">
					<ruler
							:style="[offset.base, offset.h]"
							:unit="unit"
							:length="doc.width.toPixel()"
							:scale="ratio"
							:zoom="zoom"
							orientation="horizontal"></ruler>
				</div>
				<div id="viewer"></div>
				<div class="pdf-viewer-overviewMap" v-if="view && view.url">
					<OverviewMap
						v-show="showOverview"
						:view="view"
						:generateUrl="generateThumbnailUrl"
						:onDragStop="handlePreviewerDragStop"
						:viewport="viewport"/>
				</div>
			</div>
			<div class="pdf-bottom">
				<ThumbnailsSlider
					v-if="url"
					:url="url"
					:pageCount="doc.pageCount"
					:generateUrl="generateThumbnailUrl"
					:currentPage="page"
					:changePage="changePage"/>
			</div>
		</div>
	</div>
</template>
<script type="text/javascript">

import L from 'leaflet';
import 'leaflet-loading';
import 'leaflet-loading/src/Control.Loading.css';
import { mapActions, mapGetters } from 'vuex'
import Ruler from './Ruler'
import PointMeasure from './PointMeasure'
import ThumbnailsSlider from './ThumbnailsSlider.vue';
import OverviewMap from './OverviewMap.vue';
import Inks from './Inks.vue';
import errorImage from '../assets/Tile@2x.png';

export default {
	props: [
		'spineAdjust',
		'url',
		'zoom',
		'page',
		'unit',
		'showOverview',
		'showOverprint',
		'showInks',
		'showRulers',
		'showBoxes',
		'changePage',
		'onFileLoaded',
		'selectedInks',
		'selectedBoxes',
		'zoomEnd'
	],
	components: {
		Ruler,
		OverviewMap,
		ThumbnailsSlider,
		Inks
	},
	data() {
		return {
			rasterMask: '/api/media/tile/{z}/{x}/{y}?width={width}&height={height}&ratio={ratio}&page={page}&' +
						'url={sourceUrl}&overprint={overprint}&size={size}',
			separationMask: '/api/media/tile/separated/{z}/{x}/{y}?width={width}&height={height}&ratio={ratio}&' +
							'page={page}&inks={inks}&url={sourceUrl}&overprint={overprint}&' +
							'size={size}',
			map: null,
			tileSize: 256,
			ratio: 1,
			zoom100: 5,
			pointMMRatio: 0.3527777778,
			pointsPerInch: 72,
			layers: [],
			inks: [],
			boxes: {},
			mapOptions: {
				crs: L.CRS.Simple,
				minZoom: 1,
				maxZoom: 12,
				doubleClickZoom: false,
				zoomControl: false,
				zoomSnap: 0.2,
				maxBoundsViscosity: 0.9,
				inertia: false,
				zoomAnimation:false
			},
			// domains
			view: {},
			screen: {
				dpi: 96,
				dpmm: 72
			},
			panel: {
				width: 0,
				height: 0,
				centre: {
					x: 0,
					y: 0
				},
			},
			doc: {
				pageCount: 0,
				width: new PointMeasure(0, 96),
				height: new PointMeasure(0, 96),
				topLeft: {
					x: 0,
					y: 0
				}
			},
			offset: {
				base: { position: "relative" },
				h: { left: "0" },
				v: { top: "0" }
			},
			viewport: {
				width: 1,
				height: 1
			}
		}
	},
	computed: {
		...mapGetters(['bases']),
		zoomLevel() {
			return Math.pow(2, this.zoom - this.zoom100) * 100;
		}
	},
	mounted() {
		this.patchLeaflet();
		this.map = L.map('viewer', this.mapOptions).setView([0, 0], this.zoom);
		this.map.on("zoomend", event => this.performZoom());
		this.map.on("move", event => this.moveMap());
		this.map.on("resize", event => this.getMapDimensions());
		this.map.on("load", event => this.getMapDimensions());
		this.map.on("viewreset", event => this.getMapDimensions());

		this.loadingTimeout = 8000;
		const loadingControl = L.Control.loading({
			separate: true,
			delayIndicator: this.loadingTimeout
		});
		this.map.addControl(loadingControl);

		// can we put this somewhere common
		if (this.url) {
			this.map.setZoom(this.zoom100);
			// this.doc.page = 1;
			this.loadFile();
		}

		this.init();
	},
	methods: {
		patchLeaflet() {
			// Workaround for 1px lines appearing in some browsers due to fractional transforms
			// and resulting anti-aliasing. adapted from @cmulders' solution:
			// https://github.com/Leaflet/Leaflet/issues/3575#issuecomment-150544739
			let originalInitTile = L.GridLayer.prototype._initTile;
			if (originalInitTile.isPatched) return;
			L.GridLayer.include({
				_initTile: function (tile) {
					originalInitTile.call(this, tile);
					var tileSize = this.getTileSize();
					tile.style.width = tileSize.x + 1 + 'px';
					tile.style.height = tileSize.y + 1 + 'px';
				}
			});
			L.GridLayer.prototype._initTile.isPatched = true;
		},
		init() {
			this.getMapDimensions();
			this.screen.dpi = $("#inch1").width();
			this.screen.ppmm = this.screen.dpi / 25.4;
			this.clearLayers();
		},
		moveMap() {
			this.calcCentre();
			this.calcOffset();
			this.calcViewport();
		},
		calcOffset() {
			this.offset.h.left = `${this.doc.topLeft.x}px`;
			this.offset.v.top = `${this.doc.topLeft.y}px`;
		},
		calcCentre() {
			const centre = this.map.getCenter();
			const dx = centre.lng * Math.pow(2, this.zoom);
			const dy = centre.lat * Math.pow(2, this.zoom);
			const halfDocX = (this.doc.width.toPixel() / 2) / Math.pow(2, 5 - this.zoom);
			const halfDocY = (this.doc.height.toPixel() / 2) / Math.pow(2, 5 - this.zoom);
			this.doc.topLeft = {
				x: (this.panel.centre.x - dx - halfDocX),
				y: this.panel.centre.y + dy - halfDocY
			}
		},
		calcViewport() {
			const width = this.panel.width / (this.doc.width.toPixel() / Math.pow(2, 5 - this.zoom));
			const height = this.panel.height / (this.doc.height.toPixel() / Math.pow(2, 5 - this.zoom));
			const top = -this.doc.topLeft.y / (this.doc.height.toPixel() / Math.pow(2, 5 - this.zoom));
			const left = -this.doc.topLeft.x / (this.doc.width.toPixel() / Math.pow(2, 5 - this.zoom));
			this.viewport = {
				width,
				height,
				top: top < 0 ? 0 : top,
				left: left < 0 ? 0 : left
			};
		},
		getMapDimensions() {
			const size = this.map.getSize();
			this.panel.width = size.x;
			this.panel.height = size.y;
			this.panel.centre.x = this.panel.width / 2;
			this.panel.centre.y = this.panel.height / 2;
			this.calcCentre();
		},
		setPageValues() {
			this.doc.width = new PointMeasure(_.get(this, "view.data.page.width"), this.screen.dpi);
			this.doc.height = new PointMeasure(_.get(this, "view.data.page.height"), this.screen.dpi);
			this.ratio = this.doc.width.toPixel() / this.doc.width.points;
			this.doc.pageCount = _.get(this, "view.data.file.pageCount");
		},
		async loadFile({ skipMetadata = false } = {}) {
			this.loading = true;
			setTimeout(() => {
				if (this.loading) {
					this.map.fire('dataloading');
				}
			}, this.loadingTimeout);
			if (!skipMetadata) await this.fetchMetaData();
			this.init();
			this.drawPage();
		},
		async fetchMetaData() {
			const metadataUrl = `${this.bases.rasterApi}/api/media/metadata?page=${this.page}&url=${encodeURIComponent(this.url)}`;
			const metadata = await this.$http.get(metadataUrl);
			console.log("token", metadata.body.token);
			this.view = {
				page: this.page,
				url: metadata.body.token || this.url,
				data: metadata.body
			};
			this.inks = _.get(this, 'view.data.page.inks', []);
			this.setBoxColours();

			this.onFileLoaded && this.onFileLoaded(this.view);
		},
		setBoxColours() {
			const colours = {
				MediaBox: '#00ff00',
				TrimBox: '#ff0000',
				BleedBox: '#5E35B1',
				CropBox: '#0000ff',
				ArtBox: '#ffff00'
			};
			_.each(this.view.data.page.boxes, box => box.colour = colours[box.type]);
		},
		setMaxBounds() {
			const w = this.doc.width.toPixel() / Math.pow(2, this.zoom100);
			const h = this.doc.height.toPixel() / Math.pow(2, this.zoom100);
			const pw = this.panel.width / Math.pow(2, this.zoom100);
			const ph = this.panel.height / Math.pow(2, this.zoom100);
			const minZoom = Math.log2(_.min([pw / w / 3, ph / (h + 2 * w)]) / 0.0625) + 1; //6.25% corresponds to zoom level 1
			this.map.setMaxBounds([
				[-(h + 2 * w) / 2, -3 * w / 2],
				[(h + 2 * w) / 2, 3 * w / 2]
			])
			this.map.setMinZoom(minZoom)
		},
		drawPage() {
			this.clearLayers();
			this.setPageValues();
			const layerOptions = {
				tileSize: this.tileSize,
				noWrap: true,
				sourceUrl: encodeURIComponent(this.view.url),
				page: this.page,
				width: Math.round(this.doc.width.points),
				height: Math.round(this.doc.height.points),
				ratio: this.ratio.toFixed(5),
				overprint: this.showOverprint,
				size: this.tileSize,
				errorTileUrl: errorImage
			};
			let maskUrl = this.rasterMask;
			if (this.selectedInks && this.selectedInks.length < this.inks.length) {
				layerOptions.inks = this.selectedInks.join(",");
				maskUrl = this.separationMask;
			}
			const layer = L.tileLayer(`${this.bases.rasterApi}${maskUrl}`, layerOptions).addTo(this.map);
			this.layers.push(layer);

			let redrawTimeout = 4000;
			let tileError = false;

			layer.on('tileerror', () => {
				tileError = true;
			});

			layer.on('load', () => {
				if (!tileError) {
					redrawTimeout = 4000;
				} else {
					setTimeout(() => {
						tileError = false;
						layer.redraw();
					}, redrawTimeout);
					redrawTimeout = Math.min(redrawTimeout * 1.5, 30 * 1000);
				}
				this.loading = false;
				this.map.fire('dataload');
			});

			this.drawPageBoxes();
			this.drawPageSpine();
			this.calcCentre();
			this.calcOffset();
			this.calcViewport();
			this.setMaxBounds();
		},
		clearLayers() {
			this.layers.forEach(layer => layer.remove());
			this.boxes = {};
			this.layers = [];
		},
		drawPageBoxes() {
			if (!this.view.data.page.boxes)
				return;
			const selectedBoxes = this.selectedBoxes || [];
			this.view.data.page.boxes = this.view.data.page.boxes.filter(box => !['CropBox', 'ArtBox'].includes(box.type));
			const pixelToLatLng = p => p / Math.pow(2, this.zoom);
			const pointToLatLng = d => pixelToLatLng(new PointMeasure(d, 96).toPixel(this.zoom));

			const tlx = -this.doc.width.points / 2;
			const tly = this.doc.height.points / 2;

			this.view.data.page.boxes.forEach((box) => {
				const x = pointToLatLng(tlx + box.left);
				const y = pointToLatLng(tly - box.top);
				const w = pointToLatLng(box.width);
				const h = pointToLatLng(box.height);
				const rect = [[y, x], [y, x + w], [y - h, x + w], [y - h, x], [y, x]];
				const rectangle = L.rectangle(rect, {
					color: box.colour,
					weight: 1,
					stroke: selectedBoxes.includes(box.type),
					fill: false
				});
				rectangle.addTo(this.map);
				this.boxes[box.type] = rectangle;
				this.layers.push(rectangle);
			})
		},
		drawPageSpine() {
			const spines = _.get(this.spineAdjust, 'spines');

			if (!spines) {
				return;
			}

			const pixelToLatLng = p => p / Math.pow(2, this.zoom);
			const pointToLatLng = d => pixelToLatLng(new PointMeasure(d, 96).toPixel(this.zoom));
			const x = pointToLatLng((this.doc.width.points - this.view.data.page.width) / 2);
			const yStart = pointToLatLng(1.2 * this.doc.height.points / 2);
			const yEnd = pointToLatLng(0.8 * this.doc.height.points / 2 - this.view.data.page.height);
			spines.forEach(spine => {
				if (!spine.active) {
					return;
				}

				const coordinates = [
					[
						[yStart, x - pointToLatLng(spine.value/2)],
						[yEnd, x - pointToLatLng(spine.value/2)]
					],
					[
						[yStart, x + pointToLatLng(spine.value/2)],
						[yEnd, x + pointToLatLng(spine.value/2)]
					]
				];

				const options = { weight: spine.weight, color: spine.colour };

				if (spine.dashArray) {
					options.dashArray = spine.dashArray;
				}

				if (spine.active) {
					const lines = L.polyline(coordinates, options).addTo(this.map);
					this.layers.push(lines);
				}
			});
			this.drawSpineLayers();
		},
		drawSpineLayers() {
			const originalSpine = _.find(this.spineAdjust.spines, { type: 'spine.original' });

			if (!originalSpine) {
				return;
			}

			if (!originalSpine.active) {
				return;
			}

			const pixelToLatLng = p => p / Math.pow(2, this.zoom);
			const pointToLatLng = d => pixelToLatLng(new PointMeasure(d, 96).toPixel(this.zoom));
			const tlx = pointToLatLng((this.doc.width.points - this.view.data.page.width) / 2);
			const x = pointToLatLng(this.doc.width.points / 2);
			const y = pointToLatLng(this.doc.height.points / 2);
			const w = pointToLatLng(this.view.data.page.width);
			const h = pointToLatLng(this.view.data.page.height);
			const leftRect = [[y, x - w], [y - h, tlx - pointToLatLng(originalSpine.value / 2)]];
			const rightRect = [[y, x], [y - h, tlx + pointToLatLng(originalSpine.value / 2)]];
			const fillOptions = Object.assign(
				{ color: '#fff', fillOpacity: 0.6, stroke: false },
				_.get(this.spineAdjust, 'layersOptions', {})
			);
			const lRect = L.rectangle(leftRect, fillOptions).addTo(this.map);
			const rRect = L.rectangle(rightRect, fillOptions).addTo(this.map);
			this.layers.push(lRect, rRect);
		},
		generateThumbnailUrl(page = this.page, { size = 100 } = {}) {
			if (!this.view || !this.view.url) return;
			return `${this.bases.rasterApi}/api/media/thumbnail?page=${page}&size=${size}&url=${encodeURIComponent(this.view.url)}`;
		},
		handlePreviewerDragStop({ top, left } = {}) {
			const w = this.doc.width.toPixel() / Math.pow(2, this.zoom100);
			const h = this.doc.height.toPixel() / Math.pow(2, this.zoom100);
			const lng = w * (left - 50) / 100;
			const lat = h * (-top + 50) / 100;
			this.map.setView([lat, lng]);
		},
		// navigation
		goToPage() {
			this.loadFile();
		},
		performZoom() {
			this.zoomEnd(this.map.getZoom());
			this.calcCentre();
			this.calcOffset();
			this.calcViewport();
		},
		fitTo(target) {
			const pixelToLatLng = p => p / Math.pow(2, this.zoom);
			const pointToLatLng = d => pixelToLatLng(new PointMeasure(d, 96).toPixel(this.zoom));

			const w = pointToLatLng(this.doc.width.points / 2);
			const h = pointToLatLng(this.doc.height.points / 2);
			const docBounds = L.latLngBounds(L.latLng(-h, -w), L.latLng(h, w));

			const bounds = this.boxes['MediaBox'] ? this.boxes['MediaBox'].getBounds() : docBounds;
			const centerY = this.map.getCenter().lat;

			this.map.invalidateSize()
			switch (target) {
				case 'page':
					this.map.fitBounds(bounds);
					break;
				case 'width':
					this.map.fitBounds([
						[centerY, bounds.getWest()],
						[centerY, bounds.getEast()]
					]);
					break;
				default:

			}
		}
	},
	watch: {
		spineAdjust() {
			this.drawPage();
		},
		selectedInks() {
			this.loadFile({ skipMetadata: true });
		},
		showOverprint() {
			this.loadFile({ skipMetadata: true });
		},
		selectedBoxes(boxNames) {
			_.each(this.boxes, (box, name) => {
				box.setStyle({stroke: _.includes(boxNames, name)});
			});
		},
		showInks() {
			console.log("showInks", this.showInks);
		},
		showBoxes() {
			_.each(this.boxes, box => box.setStyle({stroke: this.showBoxes}));
		},
		url(url) {
			this.map.setZoom(this.zoom100);
			this.loadFile();
		},
		zoom() {
			if (this.zoom >= 0 && this.zoom <= 12) {
				this.map.setZoom(this.zoom);
				this.performZoom();
			}
		},
		page() {
			this.loadFile();
		}
	}
}
</script>
<style lang="css">
  @import "~leaflet/dist/leaflet.css";
  .pdf {
    display: flex;
    flex-direction: row;
	background-color: #F5F5F5;
	border-right: 1px solid #F5F5F5;
	flex-grow: 1;
  }

  .pdf-container {
	  flex-grow: 1;
	  display: flex;
	  flex-direction: column;
	  position: relative;
	  overflow: hidden;
  }
  .pdf-container .pdf-viewer{
	  display: flex;
	  flex-grow: 1;
	  padding: 30px 0 0 30px;
	  position: relative;
  }
  #viewer {
	  flex-grow: 1;
  }
  .pdf-container .pdf-viewer .pdf-viewer-overviewMap{
	  position: absolute;
	  /*max-width: 200px;
	  max-height: 150px;*/
	  bottom: calc(20px);
	  left: calc(50px);
	  z-index: 1000;
	  box-shadow: rgba(0, 0, 0, 0.5) 0 0 10px 0;
  }

  .pdf-container .pdf-bottom{
	  padding: 0 0 0 30px;
  }

  .horizontal-ruler {
    position: absolute;
    top: 0;
    left: 30px;
    height: 30px;
    right: 0;
    background: whitesmoke;
	overflow:hidden;
  }

  .vertical-ruler {
    position: absolute;
    top: 30px;
    width: 30px;
    left: 0px;
    bottom: 0;
    background: whitesmoke;
	overflow:hidden;
  }

  .leaflet-top, .leaflet-bottom {
    z-index: 500 !important;
  }

  .leaflet-control-loading {
    text-align: left !important;
    background-position: 5px 50% !important;
    width: 440px !important;
    background: red;
  }

  .leaflet-control-loading:after {
    margin-left: 25px;
    content: 'Loading preview. Complex PDF documents may take longer than expected.';
  }

  #inch1 {
    background-color: red;
    width: 1in;
    height: 1in;
    display: none;
  }
</style>



// WEBPACK FOOTER //
// Pdf.vue?aeeec390